import React, { useEffect, useMemo, useState } from 'react';
import { Avatar, Button, Empty, message, Spin, Typography } from 'antd';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import RichTextComments from '../../../components/elements/KanbanBoard/RichTextComments/RichTextComments';
import '../../../components/elements/KanbanBoard/RichTextComments/RichTextComments.css';
import RichTextEditor from '../../../components/editor/RichTextEditor';
import RichTextView from '../../../components/editor/RichTextView';
import type { MentionUser } from '../../../components/editor/RichTextEditor';
import { isLegacyEditor } from '../../../components/editor/editorFlag';
import { isRichTextEmpty } from '../../../components/editor/richText';
import type { TaskStore } from '../../../stores/kanban/TaskStore';
import type { KanbanTask, KanbanTaskComment } from '../../../stores/KanbanTaskStores';
import { useDirtyGuard } from './DirtyGuard';

dayjs.extend(relativeTime);

const { Text } = Typography;

export type TaskCommentsProps = {
  task: KanbanTask;
  store: TaskStore;
  readOnly: boolean;
  mentionUsers: MentionUser[];
  currentUserId?: string;
  /** The tab label lives in `TaskMain`, which has no other view into the comment list. */
  onCountChange?: (count: number) => void;
};

function initials(comment: KanbanTaskComment): string {
  const letters = `${comment.author?.firstName?.[0] ?? ''}${comment.author?.lastName?.[0] ?? ''}`;
  return letters.toUpperCase() || '?';
}

/**
 * The drawer's Comments tab: a list (`RichTextView` per comment) plus a `RichTextEditor` composer.
 * Falls back to the untouched legacy `RichTextComments` (Editor.js) when the browser is pinned to
 * it — the two editors store incompatible content and are never mixed within one session.
 */
const TaskComments: React.FC<TaskCommentsProps> = observer(
  ({ task, store, readOnly, mentionUsers, currentUserId, onCountChange }) => {
    const { t } = useTranslation();
    const legacy = useMemo(() => isLegacyEditor(), []);

    const [comments, setComments] = useState<KanbanTaskComment[]>([]);
    const [draft, setDraft] = useState('');
    const [posting, setPosting] = useState(false);
    // `RichTextEditor` skips syncing `value` in while it's focused (so typing is never clobbered by
    // an outside update), so clearing `draft` after a Ctrl+Enter/Send submit doesn't clear what's on
    // screen. Bumping this key remounts the editor with a fresh, empty instance instead.
    const [submitCount, setSubmitCount] = useState(0);

    useEffect(() => {
      let cancelled = false;
      store
        .getTaskComments(task.id)
        .then((result) => {
          if (!cancelled) {
            setComments(result);
          }
        })
        .catch(() => undefined);
      return () => {
        cancelled = true;
      };
    }, [store, task.id]);

    useEffect(() => {
      onCountChange?.(comments.length);
    }, [comments.length, onCountChange]);

    useDirtyGuard('task-comment-composer', !legacy && !isRichTextEmpty(draft));

    const post = async (content: string): Promise<KanbanTaskComment | null> => {
      const authorId = currentUserId ? Number(currentUserId) : undefined;
      const created = await store.createComment({ taskId: task.id, content, authorId });
      if (created) {
        setComments((prev) => [...prev, created]);
      }
      return created;
    };

    if (legacy) {
      const addLegacyComment = async (content: string): Promise<boolean> => {
        setPosting(true);
        try {
          const created = await post(content);
          if (!created) {
            message.error(t('FailedToAddComment'));
            return false;
          }
          message.success(t('CommentAdded'));
          return true;
        } catch (error) {
          console.error(error);
          message.error(t('FailedToAddComment'));
          return false;
        } finally {
          setPosting(false);
        }
      };

      return (
        <Spin spinning={posting}>
          <RichTextComments
            comments={comments}
            onAddComment={addLegacyComment}
            selectedTaskId={String(task.id)}
            isReadOnly={readOnly}
            t={t}
          />
        </Spin>
      );
    }

    const handleSubmit = async (html?: string) => {
      const content = html ?? draft;
      if (posting || isRichTextEmpty(content)) {
        return;
      }
      setPosting(true);
      try {
        const created = await post(content);
        if (created) {
          setDraft('');
          setSubmitCount((count) => count + 1);
          message.success(t('CommentAdded'));
        } else {
          message.error(t('FailedToAddComment'));
        }
      } catch (error) {
        console.error(error);
        message.error(t('FailedToAddComment'));
      } finally {
        setPosting(false);
      }
    };

    return (
      <div className="task-comments">
        <div className="task-comments__list">
          {comments.length === 0 ? (
            <Empty description={t('NoCommentsYet')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            comments.map((comment) => (
              <div key={comment.id} className="task-comments__item">
                <Avatar size={28} className="task-comments__avatar">
                  {initials(comment)}
                </Avatar>
                <div className="task-comments__body">
                  <div className="task-comments__meta">
                    <Text strong className="task-comments__author">
                      {comment.author ? `${comment.author.firstName} ${comment.author.lastName}` : t('Unknown')}
                    </Text>
                    <Text type="secondary" className="task-comments__date">
                      {dayjs(comment.createdDate).fromNow()}
                    </Text>
                  </div>
                  <RichTextView value={comment.content} className="task-comments__content" />
                </div>
              </div>
            ))
          )}
        </div>

        {!readOnly && (
          <div className="task-comments__composer">
            <RichTextEditor
              key={submitCount}
              value={draft}
              onChange={setDraft}
              onSave={(html) => void handleSubmit(html)}
              minHeight={90}
              placeholder={t('taskDrawerCommentPlaceholder')}
              uploadImage={(file) => store.uploadTaskImage(task.id, file)}
              mentionUsers={mentionUsers}
            />
            <div className="task-comments__composer-actions">
              <Text type="secondary" className="task-comments__hint">
                {t('taskDrawerCommentHint')}
              </Text>
              <Button
                type="primary"
                size="small"
                loading={posting}
                disabled={isRichTextEmpty(draft)}
                onClick={() => void handleSubmit()}
              >
                {t('taskDrawerSendComment')}
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  },
);

export default TaskComments;
